import React, {
  forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Alert,
  Select,
  Table,
  Tooltip,
} from 'antd';
import {
  Envelope,
  Funnel,
  PhoneTransfer,
  Target,
} from '@phosphor-icons/react';
import Button from '../../../components/Button';
import Typography from '../../../components/Typography';
import COLORS from '../../../util/constants/colors';
import buildMairaReportColumns, { reportSortKeyForColumn } from './reportColumns';
import {
  compactParams, csrfToken, humanizeSnakeCase, mairaFilterParams,
} from '../helpers';
import ReportFilterInputs from './ReportFilterInputs';
import useLatestRequest from '../useLatestRequest';
import { captureAnalytics } from '../../../actions/preload';

/* global toastr */

// Destination filter options mirror the dashboard's destination rows one-to-one:
// `other` selects the literal 'other' row, `unknown` selects the NULL/blank/
// literal-'unknown' row — so a single selection reproduces the displayed row.
export const TRANSFER_DESTINATION_CATEGORY_FILTER_KEYS = [
  'clinic',
  'billing',
  'insurance',
  'medical_records',
  'spanish_line',
  'other',
  'unknown',
];

export const CALLER_OBJECTIVE_FILTER_KEYS = [
  'scheduling',
  'rescheduling_cancellation',
  'billing',
  'insurance',
  'medical_records',
  'prescription_refill',
  'clinical_question',
  'lab_results',
  'provider_staff_request',
  'other',
  'unknown',
];

// ENG-2926: offer the curated known keys plus any labels actually observed in
// the data (server-provided), so a new voice-agent taxonomy label becomes
// filterable without a frontend deploy. The voice agent owns the taxonomy.
const classifierFilterOptions = (staticKeys, observedKeys = []) => {
  const keys = [...staticKeys];
  (observedKeys || []).forEach((key) => {
    if (key && !keys.includes(key)) keys.push(key);
  });
  return keys.map((key) => ({
    value: key,
    label: humanizeSnakeCase(key),
  }));
};

const cloneReportFilters = (filters) => ({
  dateRange: { ...filters.dateRange },
  selectedVoiceAiNumberIds: [...filters.selectedVoiceAiNumberIds],
  selectedDepartmentSlugs: [...filters.selectedDepartmentSlugs],
  selectedTransferDestinationCategories: [...(filters.selectedTransferDestinationCategories ?? [])],
  selectedCallerObjectives: [...(filters.selectedCallerObjectives ?? [])],
  sentimentBucket: filters.sentimentBucket || null,
  outcome: filters.outcome || null,
});

const hasCompleteDateRange = (filters) => Boolean(filters.dateRange.startDate && filters.dateRange.endDate);

const MairaReportBuilder = forwardRef(({
  dateRange,
  dateRangeResolved,
  selectedVoiceAiNumberIds,
  selectedDepartmentSlugs,
  filterOptions,
  transferBreakdownEnabled,
  taxonomy,
}, ref) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  const [hasBuiltReport, setHasBuiltReport] = useState(false);
  const [reportSort, setReportSort] = useState(null);
  const [reportDateRange, setReportDateRange] = useState({
    startDate: dateRange.startDate || null,
    endDate: dateRange.endDate || null,
  });
  const [reportVoiceAiNumberIds, setReportVoiceAiNumberIds] = useState(() => [...selectedVoiceAiNumberIds]);
  const [reportDepartmentSlugs, setReportDepartmentSlugs] = useState(() => [...selectedDepartmentSlugs]);
  const [reportTransferDestinationCategories, setReportTransferDestinationCategories] = useState([]);
  const [reportCallerObjectives, setReportCallerObjectives] = useState([]);
  const [reportSentimentBucket, setReportSentimentBucket] = useState(null);
  const [reportOutcome, setReportOutcome] = useState(null);
  const [appliedReportFilters, setAppliedReportFilters] = useState(null);
  const runLatestReportRequest = useLatestRequest();
  const initializedReportFiltersRef = useRef(false);
  const containerRef = useRef(null);
  const voiceAiNumbers = filterOptions?.voice_ai_numbers || [];
  const departments = filterOptions?.departments || [];
  const draftReportFilters = {
    dateRange: reportDateRange,
    selectedVoiceAiNumberIds: reportVoiceAiNumberIds,
    selectedDepartmentSlugs: reportDepartmentSlugs,
    selectedTransferDestinationCategories: reportTransferDestinationCategories,
    selectedCallerObjectives: reportCallerObjectives,
    sentimentBucket: reportSentimentBucket,
    outcome: reportOutcome,
  };
  const exportReportFilters = appliedReportFilters || draftReportFilters;
  const hasDraftDateRange = dateRangeResolved && hasCompleteDateRange(draftReportFilters);
  const hasExportDateRange = dateRangeResolved && hasCompleteDateRange(exportReportFilters);

  useEffect(() => {
    if (!dateRangeResolved || initializedReportFiltersRef.current) return;

    initializedReportFiltersRef.current = true;
    setReportDateRange({
      startDate: dateRange.startDate || null,
      endDate: dateRange.endDate || null,
    });
    setReportVoiceAiNumberIds([...selectedVoiceAiNumberIds]);
    setReportDepartmentSlugs([...selectedDepartmentSlugs]);
  }, [dateRange.startDate, dateRange.endDate, dateRangeResolved, selectedDepartmentSlugs, selectedVoiceAiNumberIds]);

  // ENG-2926: when the breakdown is turned off (e.g. a mid-session flag flip),
  // clear the transfer label selections so they can't resurface if it is
  // re-enabled. This covers BOTH the visible draft selects AND the applied
  // snapshot that export/pagination/sort read from -- otherwise a
  // build -> flag-off -> flag-on cycle leaves stale transfer filters in
  // appliedReportFilters and silently sends them once the live-flag gate in
  // filterPayload reopens. The server ignores them while off, so this is
  // presentation/state hygiene only.
  useEffect(() => {
    if (!transferBreakdownEnabled) {
      setReportTransferDestinationCategories([]);
      setReportCallerObjectives([]);
      setAppliedReportFilters((prev) => (prev ? {
        ...prev,
        selectedTransferDestinationCategories: [],
        selectedCallerObjectives: [],
      } : prev));
    }
  }, [transferBreakdownEnabled]);

  // ENG-2926: payload = the shared MAIRA filter params (date/voice/dept/sentiment/
  // outcome) plus the transfer label filters. Old mode never sends the transfer
  // filters (the server would ignore them anyway); the outer compactParams drops
  // the empty arrays.
  const filterPayload = (filters) => compactParams({
    ...mairaFilterParams(filters),
    transfer_destination_categories: transferBreakdownEnabled ? filters.selectedTransferDestinationCategories : [],
    caller_objectives: transferBreakdownEnabled ? filters.selectedCallerObjectives : [],
  });

  const reportPayload = (filters, newPage = page, newPageSize = pageSize, sortState = reportSort) => compactParams({
    ...filterPayload(filters),
    page: newPage,
    page_size: newPageSize,
    sort: sortState?.sort || null,
    sort_direction: sortState?.direction || null,
  });

  const fetchReport = ({
    filters,
    newPage,
    newPageSize,
    applyFilters,
    newSort = reportSort,
  }) => {
    if (!dateRangeResolved || !hasCompleteDateRange(filters)) return;

    const requestedFilters = cloneReportFilters(filters);

    setLoading(true);
    setError(null);
    runLatestReportRequest({
      request: () => axios({
        method: 'POST',
        url: '/api/v1/maira_analytics',
        data: reportPayload(requestedFilters, newPage, newPageSize, newSort),
        headers: {
          'X-CSRF-Token': csrfToken(),
        },
      }),
      onResolve: (response) => {
        setRows(response.data.rows || []);
        setTotal(response.data.total || 0);
        setPage(response.data.page || newPage);
        setPageSize(response.data.page_size || newPageSize);
        setHasBuiltReport(true);
        if (applyFilters) {
          setAppliedReportFilters(requestedFilters);
        }
      },
      onReject: (requestError) => {
        console.error(requestError);
        setError(requestError.response?.data?.error || 'Unable to build MAIRA report.');
      },
      onFinally: () => setLoading(false),
    });
  };

  const buildReport = (newPage = 1, newPageSize = pageSize) => {
    fetchReport({
      filters: draftReportFilters,
      newPage,
      newPageSize,
      applyFilters: true,
    });
    // Manual "Build Report" only, and fired after the fetch so a capture error
    // can never block the report. Chart drill-downs reach the report via
    // buildForSegment and are tracked by maira_chart_drilldown_clicked, so they
    // are deliberately not double-counted here. Properties are low-cardinality
    // booleans/counts describing which filters were active -- never filter values
    // or PHI.
    captureAnalytics('maira_report_built', {
      has_transfer_destination: draftReportFilters.selectedTransferDestinationCategories.length > 0,
      has_caller_objective: draftReportFilters.selectedCallerObjectives.length > 0,
      has_sentiment: draftReportFilters.sentimentBucket != null,
      has_outcome: draftReportFilters.outcome != null,
      voice_number_count: draftReportFilters.selectedVoiceAiNumberIds.length,
      department_count: draftReportFilters.selectedDepartmentSlugs.length,
      transfer_breakdown_enabled: transferBreakdownEnabled,
    });
  };

  const paginateReport = (newPage, newPageSize) => {
    if (!appliedReportFilters) return;

    fetchReport({
      filters: appliedReportFilters,
      newPage,
      newPageSize,
      applyFilters: false,
    });
  };

  // A dashboard chart click drills the report into the clicked segment. The
  // dashboard invokes this directly with the filters the user is viewing, so the
  // values are a fresh click-time snapshot by construction -- no token/effect dance,
  // no stale-closure risk. fetchReport no-ops until the date range resolves, and
  // charts only render after the dashboard has loaded, so a pre-resolution call
  // cannot happen through the UI.
  useImperativeHandle(ref, () => ({
    buildForSegment: ({
      dateRange: segmentDateRange,
      selectedVoiceAiNumberIds: segmentVoiceAiNumberIds,
      selectedDepartmentSlugs: segmentDepartmentSlugs,
      selectedTransferDestinationCategories: segmentTransferDestinationCategories = [],
      selectedCallerObjectives: segmentCallerObjectives = [],
      sentimentBucket,
      outcome,
    }) => {
      const nextFilters = {
        dateRange: {
          startDate: segmentDateRange.startDate || null,
          endDate: segmentDateRange.endDate || null,
        },
        selectedVoiceAiNumberIds: [...segmentVoiceAiNumberIds],
        selectedDepartmentSlugs: [...segmentDepartmentSlugs],
        // A transfer drill-down (destination row / objective sub-row) carries
        // these; a sentiment/outcome drill-down passes empty arrays, which
        // clears any prior selections so the Select inputs match the report
        // that just ran.
        selectedTransferDestinationCategories: [...segmentTransferDestinationCategories],
        selectedCallerObjectives: [...segmentCallerObjectives],
        sentimentBucket: sentimentBucket || null,
        outcome: outcome || null,
      };

      setReportDateRange(nextFilters.dateRange);
      setReportVoiceAiNumberIds(nextFilters.selectedVoiceAiNumberIds);
      setReportDepartmentSlugs(nextFilters.selectedDepartmentSlugs);
      setReportTransferDestinationCategories(nextFilters.selectedTransferDestinationCategories);
      setReportCallerObjectives(nextFilters.selectedCallerObjectives);
      setReportSentimentBucket(nextFilters.sentimentBucket);
      setReportOutcome(nextFilters.outcome);

      fetchReport({
        filters: nextFilters,
        newPage: 1,
        newPageSize: pageSize,
        applyFilters: true,
      });

      containerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    },
  }));

  const sortReport = (sorter, newPageSize) => {
    if (!appliedReportFilters) return;

    const sortKey = sorter?.order ? reportSortKeyForColumn(sorter.columnKey || sorter.field) : null;
    const nextSort = sortKey
      ? { sort: sortKey, direction: sorter.order === 'ascend' ? 'asc' : 'desc' }
      : null;

    setReportSort(nextSort);
    fetchReport({
      filters: appliedReportFilters,
      newPage: 1,
      newPageSize,
      applyFilters: false,
      newSort: nextSort,
    });
  };

  const emailReport = () => {
    if (!hasExportDateRange) return;

    setExporting(true);
    setError(null);
    axios({
      method: 'POST',
      url: '/api/v1/maira_analytics/export_report',
      data: filterPayload(exportReportFilters),
      headers: {
        'X-CSRF-Token': csrfToken(),
      },
    }).then(() => {
      toastr.success('An email with your MAIRA analytics export will be sent shortly');
    }).catch((requestError) => {
      console.error(requestError);
      setError(requestError.response?.data?.error || 'Unable to email MAIRA analytics export.');
    }).finally(() => {
      setExporting(false);
    });
  };

  return (
    <div className="maira-report-builder" ref={containerRef}>
      <div className="maira-report-header">
        <Typography variant="text" size="medium" bold>
          <Funnel color={COLORS.BLUE_PRIMARY_LITE} size={16} weight="bold" />
          &nbsp;
          MAIRA Report Builder
        </Typography>
      </div>
      <div className="maira-report-inputs">
        <ReportFilterInputs
          dateRange={reportDateRange}
          onDateRangeChange={setReportDateRange}
          voiceAiNumberIds={reportVoiceAiNumberIds}
          onVoiceAiNumberIdsChange={setReportVoiceAiNumberIds}
          departmentSlugs={reportDepartmentSlugs}
          onDepartmentSlugsChange={setReportDepartmentSlugs}
          sentimentBucket={reportSentimentBucket}
          onSentimentBucketChange={setReportSentimentBucket}
          outcome={reportOutcome}
          onOutcomeChange={setReportOutcome}
          voiceAiNumbers={voiceAiNumbers}
          departments={departments}
          taxonomy={taxonomy}
        />
        {transferBreakdownEnabled && (
          <div className="maira-report-input-item">
            <Tooltip title="Transfer destination applies only to transferred calls. Selecting Unknown includes transferred calls with no recorded destination.">
              <Typography variant="text" size="small" bold>
                <PhoneTransfer size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
                &nbsp;
                Transfer Destination
              </Typography>
            </Tooltip>
            <Select
              mode="multiple"
              allowClear
              placeholder="All destinations"
              data-testid="report-filter-transfer-destination"
              value={reportTransferDestinationCategories}
              options={classifierFilterOptions(TRANSFER_DESTINATION_CATEGORY_FILTER_KEYS, filterOptions?.transfer_destination_categories)}
              onChange={setReportTransferDestinationCategories}
            />
          </div>
        )}
        {transferBreakdownEnabled && (
          <div className="maira-report-input-item">
            <Tooltip title="Caller objective is classified for every call, so this filter applies to all calls, not just transfers.">
              <Typography variant="text" size="small" bold>
                <Target size={16} color={COLORS.BLUE_PRIMARY_LITE} weight="bold" />
                &nbsp;
                Caller Objective (All Calls)
              </Typography>
            </Tooltip>
            <Select
              mode="multiple"
              allowClear
              placeholder="All objectives"
              data-testid="report-filter-caller-objective"
              value={reportCallerObjectives}
              options={classifierFilterOptions(CALLER_OBJECTIVE_FILTER_KEYS, filterOptions?.caller_objectives)}
              onChange={setReportCallerObjectives}
            />
          </div>
        )}
        <div className="maira-report-actions">
          <Button
            type="primary"
            size="xsmall"
            label="Build Report"
            onClick={() => buildReport(1, pageSize)}
            disabled={loading || !hasDraftDateRange}
          />
          <Button
            type="secondary"
            size="xsmall"
            label="Email CSV"
            leftIcon={<Envelope size={16} color={exporting ? COLORS.TEXT_DISABLED_LITE : COLORS.PURPLE_PRIMARY_LITE} />}
            onClick={emailReport}
            disabled={exporting || !hasExportDateRange}
          />
        </div>
      </div>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
        />
      )}
      <Table
        rowKey="id"
        columns={buildMairaReportColumns({
          sortBy: reportSort?.sort || null,
          sortDirection: reportSort?.direction || null,
          sortingEnabled: hasBuiltReport,
        }, { transferBreakdownEnabled })}
        dataSource={rows}
        loading={loading}
        scroll={{ x: 1780 }}
        locale={{ emptyText: hasBuiltReport ? 'No calls found for these filters' : 'Build a report to view call rows' }}
        pagination={{
          pageSize,
          current: page,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          total,
          showTotal: (count, range) => `${range[0]}-${range[1]} of ${count} calls`,
          position: ['bottomCenter'],
        }}
        onChange={(pagination, _tableFilters, sorter, extra) => {
          if (extra?.action === 'sort') {
            sortReport(sorter, pagination.pageSize);
            return;
          }

          paginateReport(pagination.current, pagination.pageSize);
        }}
      />
    </div>
  );
});

MairaReportBuilder.displayName = 'MairaReportBuilder';

MairaReportBuilder.propTypes = {
  dateRange: PropTypes.object.isRequired,
  dateRangeResolved: PropTypes.bool,
  selectedVoiceAiNumberIds: PropTypes.array.isRequired,
  selectedDepartmentSlugs: PropTypes.array.isRequired,
  filterOptions: PropTypes.object,
  transferBreakdownEnabled: PropTypes.bool,
  taxonomy: PropTypes.object,
};

MairaReportBuilder.defaultProps = {
  dateRangeResolved: false,
  filterOptions: {},
  transferBreakdownEnabled: false,
  taxonomy: {},
};

export default MairaReportBuilder;
